import type { RootState } from 'src/redux/store';
import type { UserPresenceSocketPayload } from 'src/types/user-presence';

import { useEffect } from 'react';

import { useWebSocket } from 'src/contexts/websocket-context';
import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { getUserRole } from 'src/pages/auth/hooks/permission-utils';
import { usePermissionChecks } from 'src/pages/auth/hooks/usePermissionChecks';
import { usePermissionsSource } from 'src/pages/auth/hooks/usePermissionsSource';
import {
  setUserOnline,
  clearUserPresence,
} from 'src/redux/slices/userPresenceSlice';

/**
 * Single app-wide Socket.IO listener for `user.presence` → Redux.
 * Must render under WebSocketProvider + Redux Provider.
 */
export function UserPresenceSocketBridge() {
  const dispatch = useAppDispatch();
  const { subscribe } = useWebSocket();
  const loginApiData = useAppSelector((state: RootState) => state.auth.loginApiData);
  const permissions = usePermissionsSource(loginApiData);
  const userRole = getUserRole(loginApiData);
  const { hasPermission } = usePermissionChecks(permissions, userRole);
  const canViewUserPresence = hasPermission('userprofile');

  useEffect(() => {
    if (!canViewUserPresence) {
      dispatch(clearUserPresence());
      return undefined;
    }

    const handler = (payload: UserPresenceSocketPayload) => {
      const userId = payload?.userId;
      if (!userId || (payload.status !== 'online' && payload.status !== 'offline')) {
        return;
      }
      dispatch(
        setUserOnline({
          userId,
          online: payload.status === 'online',
        })
      );
    };

    return subscribe('user.presence', handler);
  }, [canViewUserPresence, dispatch, subscribe]);

  return null;
}
